import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const selectedMediaId = this.getCurrentMediaId();
    this.replaceWith(newMediaGallery);

    if (!selectedMediaId) return;

    requestAnimationFrame(() => {
      if (newMediaGallery instanceof MediaGallery) {
        newMediaGallery.restoreMediaById(selectedMediaId);
      }
    });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  getCurrentMediaId() {
    if (this.slideshow?.slides?.length) {
      const currentSlide = this.slideshow.slides[this.slideshow.current];
      const slideId = currentSlide?.getAttribute('slide-id');
      if (slideId) return slideId;
    }

    const mediaElements = this.media || [];
    if (!mediaElements.length) return null;

    const viewportCenter = window.innerHeight / 2;
    let closestMedia = null;
    let smallestDistance = Number.POSITIVE_INFINITY;

    mediaElements.forEach((mediaElement) => {
      const rect = mediaElement.getBoundingClientRect();
      const mediaCenter = rect.top + rect.height / 2;
      const distance = Math.abs(mediaCenter - viewportCenter);

      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestMedia = mediaElement;
      }
    });

    return closestMedia?.dataset?.mediaId || null;
  }

  restoreMediaById(mediaId) {
    if (!mediaId) return;

    if (this.slideshow?.slides?.length) {
      this.slideshow.select({ id: String(mediaId) }, undefined, { animate: false });
      return;
    }

    const targetMedia = (this.media || []).find((mediaElement) => mediaElement.dataset.mediaId === String(mediaId));
    targetMedia?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
